import { CircularProgress } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { useMemo } from 'react';
import LeafLabel from '~/components/TreeView/LeafLabel';
import LeafTransition from '~/components/TreeView/LeafTransition';
import { BlobNode } from '~/hooks/auth/useAzureBlobContainers';

function FileIcon({
    node,
    downloading,
}: {
    node: BlobNode;
    downloading: string | undefined;
}) {
    const downloadingChilds = downloading?.includes(node.fullPath);
    if (downloadingChilds) {
        return (
            <CircularProgress sx={{ position: 'absolute', top: 4 }} size={12} />
        );
    } else
        return (
            <Download size={14} color={downloading ? 'gray' : 'currentColor'} />
        );
}

const LeafRenderer = ({
    node,
    nodeIdPrefix,
    onFileDownload,
    downloading,
}: {
    node: BlobNode;
    nodeIdPrefix: string;
    onFileDownload: (
        containerName: string,
        blobPaths: string[]
    ) => Promise<void>;
    downloading?: string;
}) => {
    const childHasNoDirs = useMemo(
        () => !node.children.find((child) => child.isDirectory),
        [node]
    );
    const handleDownload = () => {
        if (!downloading && childHasNoDirs) {
            const childPaths = node.children.map((child) => child.fullPath);
            onFileDownload(node.container, childPaths);
        }
    };

    return (
        node.isDirectory && (
            <TreeItem
                onClick={handleDownload}
                key={`${nodeIdPrefix}-${node.fullPath}`}
                nodeId={`${nodeIdPrefix}-${node.fullPath}`}
                TransitionComponent={LeafTransition}
                sx={{
                    position: 'relative',
                }}
                label={
                    <LeafLabel
                        label={node.name}
                        tooltip={
                            node.isDirectory && childHasNoDirs && !downloading
                                ? 'Download'
                                : undefined
                        }
                    />
                }
                expandIcon={
                    childHasNoDirs ? (
                        <FileIcon node={node} downloading={downloading} />
                    ) : (
                        <ChevronRight />
                    )
                }
                collapseIcon={
                    childHasNoDirs ? (
                        <FileIcon node={node} downloading={downloading} />
                    ) : (
                        <ChevronDown />
                    )
                }
            >
                {node.children.map((child) => (
                    <LeafRenderer
                        key={`${nodeIdPrefix}-${child.fullPath}`}
                        node={child}
                        nodeIdPrefix={`${nodeIdPrefix}-${node.fullPath}`}
                        onFileDownload={onFileDownload}
                        downloading={downloading}
                    />
                ))}
            </TreeItem>
        )
    );
};

export default LeafRenderer;
